import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { getSessionId } from 'environments/environment-helpers';
import { ToastController } from 'providers/toast-controller';
import { ToastType } from 'components/toast/toast-host';
import { AuthService } from 'services/auth.service';
import { SettingsService } from 'services/settings.service';
import { UserDb } from 'services/user-db.service';
import { DatabaseState, DatabaseWrapperProvider } from 'services/database-wrapper';
import { PubSubService } from 'services/pubsub.service';
import { ConfigDbService } from 'services/config-db.service';
import { LoggingService } from 'services/logging.service';
import { TelemetryService } from 'services/telemetry.service';
import { getMyApp } from 'app/app-interface';
import {
  ConnectionState,
  connectionStateChangedEvent,
  FIRST_PROBE_GATE_TIMEOUT_MS,
  OnlineStateService,
} from 'services/online-state.service';
import { FocusState, focusStateChangedEvent } from 'services/focus-state.service';
import { ConnectionStateService, DebouncedCloudStateChangeEvent } from 'services/connection-state.service';
import { ConnectivityResumeFlow, ConnectivityResumePorts } from 'services/connectivity-resume';
import { PullCatchupStats } from 'services/sync-health-decisions';

/**
 * Connectivity, connection toasts and app resume (first slice of deferred
 * finding df-7b66acbeb341).
 *
 * This class is only the wiring. It supplies the real connection services, the
 * real database layer, the real toasts and the real telemetry. What actually
 * happens — which toast a transition deserves, what runs when the app comes
 * back to the front, when the six-minute health tick may do work — is in
 * `ConnectivityResumeFlow` (services/connectivity-resume.ts), which has no
 * Angular in it so the unit-test runner can load it.
 *
 * Every `=== SomeEnum.Value` comparison lives here rather than in the flow,
 * because the enums involved — `DatabaseState`, `ConnectionState` and
 * `FocusState` — come from files that cannot be loaded under node. The flow
 * works in plain booleans; see its header comment.
 *
 * Lifted out of `AppComponent` with no behavior change. That component still
 * owns WHEN each of these runs during boot; see design-docs/boot-sequence.md.
 */
@Injectable({ providedIn: 'root' })
export class ConnectivityResumeService {
  private readonly flow: ConnectivityResumeFlow;

  constructor(
    private toastCtrl: ToastController,
    private settings: SettingsService,
    private auth: AuthService,
    private userDb: UserDb,
    private dbProvider: DatabaseWrapperProvider,
    private pubSub: PubSubService,
    private onlineStateService: OnlineStateService,
    // Read for the debounced cloud state below — but ALSO the thing that gets this service
    // built at all. AppComponent used to inject it directly, with a comment saying it was
    // injected "to ensure it is initialized", and AppComponent injects THIS service in its
    // own constructor, so the chain is unbroken. Do not drop this parameter on the grounds
    // that something else could supply the one value it is read for.
    private connectionStateService: ConnectionStateService,
    private configDb: ConfigDbService,
    private logging: LoggingService,
    private telemetry: TelemetryService,
  ) {
    const ports: ConnectivityResumePorts = {
      subscribeToConnectionState: (onChanged) =>
        this.pubSub.subscribe(connectionStateChangedEvent, (state: ConnectionState) =>
          onChanged(state === ConnectionState.Online)),
      subscribeToFocusGained: (onFocusGained) =>
        this.pubSub.subscribe(focusStateChangedEvent, (state: FocusState) => {
          // focus LOSS is not reported onwards — nothing in the flow acts on it
          if (state === FocusState.On) onFocusGained();
        }),
      subscribeToDebouncedCloudState: (onChanged) =>
        this.pubSub.subscribe(DebouncedCloudStateChangeEvent, (data: { state: DatabaseState }) =>
          onChanged(data.state === DatabaseState.Offline)),

      isCloudStateOffline: () => this.connectionStateService.debouncedState === DatabaseState.Offline,
      isConnectionOnline: () => this.onlineStateService.connectionState === ConnectionState.Online,
      msSinceLastProbe: () => this.onlineStateService.msSinceLastProbe(),
      isDocumentHidden: () => document.hidden,
      // the raw browser flag is exactly what the resume probe wants to probe AGAINST — see the
      // comment at its call site in the flow half.
      // eslint-disable-next-line no-restricted-syntax -- see the note above
      isBrowserOnline: () => navigator.onLine,
      hasSignedInUser: () => !!this.auth.user,
      currentUserUid: () => this.auth.user?.uid,
      nowMs: () => performance.now(),

      awaitFirstProbeAsync: () => this.onlineStateService.awaitFirstProbeAsync(FIRST_PROBE_GATE_TIMEOUT_MS),
      verifyOnlineStatusAsync: async (expectOnline, confidence) => {
        const verified = await this.onlineStateService.verifyOnlineStatus(
          expectOnline ? ConnectionState.Online : ConnectionState.Offline,
          confidence,
        );
        return verified === ConnectionState.Online;
      },

      areUserDatabasesOffline: () => this.dbProvider.userDatabasesStatus() === DatabaseState.Offline,
      // the database handle does not exist until the boot sequence creates it, so every port
      // that needs it reads it lazily rather than capturing it when this service is constructed
      verifyPushCheckpointIntegrityAsync: () => {
        void this.userDb?.database?.verifyPushCheckpointIntegrityAsync();
      },
      ensureSyncsActive: () => this.dbProvider.ensureSyncsActive(),
      ensurePullCatchupAsync: (): Promise<PullCatchupStats> | undefined =>
        this.userDb?.database?.ensurePullCatchupAsync(),
      setAllDatabasesStatusOffline: () => this.dbProvider.setAllDatabasesStatusOffline(),
      flushPendingReactiveStatusUpdates: () => this.userDb?.flushPendingReactiveStatusUpdates(),
      scanForConflictsAsync: (autoResolveEnabled, archiveRestoreEnabled) => {
        void this.userDb?.database?.scanForConflictsAsync(autoResolveEnabled, archiveRestoreEnabled);
      },
      getConfigValue: (key) => this.configDb.getConfigValue(key),

      showWentOfflineToast: () => {
        this.toastCtrl.showToast({
          uniqueId: 'offline-state-toast',
          message: LL.home.hints.offlineNow(),
          type: ToastType.WarningLight,
          duration: 5000,
          position: 'top',
          // both buttons are asked for on purpose: without an explicit close, a toast whose only
          // button is "Never show again" offers no plain way out, so the emphasised button ends up
          // being the permanent switch-off. Dismiss is the filled one because there is no action.
          showCloseButton: true,
          showDontShowAgainButton: true
        });
      },
      showBackOnlineToast: () => {
        this.toastCtrl.showToast({
          uniqueId: 'back-online-toast',
          message: LL.common.messages.backOnline(),
          type: ToastType.SuccessLight,
          duration: 3000,
          position: 'top',
          // see the offline toast above: an explicit close keeps "Never show again" from being
          // the only button on the row.
          showCloseButton: true,
          showDontShowAgainButton: true
        });
      },
      isOfflineToastDismissed: () => this.settings.isToastDismissed('offline-state-toast'),

      readLastFocusedTabId: () => localStorage.getItem('lastFocusedTabId'),
      writeLastFocusedTabId: (tabId) => localStorage.setItem('lastFocusedTabId', tabId),
      currentTabId: () => getSessionId(),

      // raw setTimeout, not safeTimeout: this service lives for the whole session, exactly like
      // the never-destroyed root shell the tick chain used to live on.
      setTimer: (callback, delayMs) => setTimeout(callback, delayMs),

      reloadAppInPlace: () => {
        // never returns and never rejects by contract — the page is replaced
        void getMyApp().reloadAppAsync({ resetToRoot: false });
      },
      reportProblem: (name, context) => this.logging.trackSilentProblem(name, context),
      reportInfo: (name, context) => this.logging.trackSilentInfo(name, context),
      trackEvent: (name, properties) => this.telemetry.trackEvent(name, properties),
    };

    this.flow = new ConnectivityResumeFlow(ports);
  }

  /** Announce the offline / back-online transitions from app level (F8 #3991). */
  subscribeToCloudStateToasts(): void {
    this.flow.subscribeToCloudStateToasts();
  }

  /**
   * Subscribe to the connection + focus events, and seed the offline state (F8b).
   * Idempotent — both boot paths call it, and warm resume falls back to the
   * cold-start path in its own catch.
   */
  subscribeToConnectionAndFocusEvents(): void {
    this.flow.subscribeToConnectionAndFocusEvents();
  }

  /**
   * Start the six-minute foreground sync-health tick chain (#3987 A5).
   * Calling it more than once does nothing, so a re-entrant boot cannot spawn
   * parallel timer chains.
   */
  startSyncHealthTick(): void {
    this.flow.startSyncHealthTick();
  }
}
